import type { FNode } from '../dom/types'

/**
 * Object pool for FNode instances.
 *
 * The renderer historically allocates a fresh FNode per `f()` call and per
 * render pass. Under list-heavy workloads (5k-10k rows) this produces tens of
 * thousands of short-lived allocations per update cycle, which makes the V8
 * minor GC fire frequently. The pool recycles released nodes so subsequent
 * `acquire()` calls hit a warm slot instead of allocating.
 *
 * Lifecycle:
 *   acquire(type)     populate fields → use in render
 *   release(node)     called by renderer on node removal → node returns to pool
 *
 * Bounded by `cap` (default 1000) to avoid the pool itself becoming a memory
 * leak. Over-cap releases are dropped on the floor.
 *
 * Stale-data prevention: every released node has all fields wiped before it
 * goes back on the stack so the next consumer cannot accidentally observe the
 * previous tenant's props/children.
 */
export class FNodePool {
  private readonly _pool: FNode[] = []
  private readonly _cap: number

  constructor(cap = 1000) {
    this._cap = cap
  }

  get size(): number {
    return this._pool.length
  }

  /**
   * Return a fresh or recycled FNode. The `type` field is set to the argument;
   * all other fields are zeroed by the prior `release()` call.
   */
  acquire(type: FNode['type']): FNode {
    if (this._pool.length > 0) {
      const node = this._pool.pop()!
      node.type = type
      return node
    }
    return { type, props: {}, children: [], key: undefined }
  }

  /**
   * Return `node` to the pool after zeroing its fields. Dropped silently when
   * the pool is at cap. Idempotent — releasing the same node twice in a row
   * leaves the pool size unchanged (only first release stores it).
   */
  release(node: FNode): void {
    if (this._pool.length >= this._cap) return
    // Idempotency: if the same object reference is already in the pool, skip.
    // A linear scan is acceptable because the pool is small in practice.
    for (let i = 0; i < this._pool.length; i++) {
      if (this._pool[i] === node) return
    }
    // Zero every field before storing — prevents stale-data leakage.
    node.type = ''
    node.props = {}
    node.children = []
    node.key = undefined
    this._pool.push(node)
  }

  /** Reset the pool — used by tests for deterministic state. */
  clear(): void {
    this._pool.length = 0
  }
}

/**
 * Module-level singleton used by the renderer. Tests can call `clear()` to
 * reset between cases. Real consumers should not construct a second pool —
 * sharing one instance maximizes recycle hits.
 */
export const fnodePool = new FNodePool()
